import { classNames } from '@schuelerhilfe/shared/src/utils/classNames';
import { formatFullDate } from '@schuelerhilfe/shared/src/utils/dayjs';
import { Button } from '@/components/Button';
import { TimeSlotMode } from '@/lib/appointments';
import type { AppointmentDay } from '@/lib/appointments';

interface TimePickerProps {
  day: AppointmentDay;
  selectedTime: string | null;
  selectedMode: TimeSlotMode | null;
  onSelect: (selection: { time: string; mode: TimeSlotMode }) => void;
  onBack: () => void;
  onNext: () => void;
  backLabel: string;
  ctaLabel: string;
  stackButtons?: boolean;
  /** CMS heading override, supports `{day}` and `{date}` placeholders. */
  heading?: string;
  /** CMS label override for the in-person slot group. */
  locationGroupLabel?: string;
  /** CMS label override for the phone slot group. */
  phoneGroupLabel?: string;
}

export const TimePicker = ({
  day,
  selectedTime,
  selectedMode,
  onSelect,
  onBack,
  onNext,
  backLabel,
  ctaLabel,
  stackButtons,
  heading,
  locationGroupLabel,
  phoneGroupLabel,
}: TimePickerProps) => (
  <div>
    <p className="h3 mb-4 text-center">
      {heading
        ? heading
            .replace('{day}', day.day)
            .replace('{date}', formatFullDate(day.dateIso))
        : `Uhrzeit am ${day.day} ${formatFullDate(day.dateIso)} wählen`}
    </p>

    <div className="mx-auto mb-8 space-y-6">
      {day.timesLocation.length > 0 && (
        <div>
          {day.timesPhone.length > 0 && (
            <p className="mb-3 text-sm font-medium text-gray-600">
              {locationGroupLabel || 'Vor Ort'}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-6">
            {day.timesLocation.map((time) => (
              <button
                key={`location-${time}`}
                type="button"
                onClick={() => onSelect({ time, mode: TimeSlotMode.Location })}
                className={classNames(
                  'flex shrink-0 grow-0 basis-[calc((100%-3rem)/3)] flex-col items-center rounded-2xl px-4 py-4 text-sm font-medium transition-colors md:basis-[calc((100%-7.5rem)/6)]',
                  selectedTime === time &&
                    selectedMode === TimeSlotMode.Location
                    ? 'bg-primary text-white'
                    : 'bg-[rgba(40,25,10,0.1)]',
                )}
              >
                <span>{time}</span>
                <span>Uhr</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {day.timesPhone.length > 0 && (
        <div>
          <p className="mb-3 text-sm font-medium text-gray-600">
            {phoneGroupLabel || 'Telefonisch'}
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {day.timesPhone.map((time) => (
              <button
                key={`phone-${time}`}
                type="button"
                onClick={() => onSelect({ time, mode: TimeSlotMode.Phone })}
                className={classNames(
                  'flex shrink-0 grow-0 basis-[calc((100%-3rem)/3)] flex-col items-center rounded-2xl px-4 py-4 text-sm font-medium transition-colors md:basis-[calc((100%-7.5rem)/6)]',
                  selectedTime === time && selectedMode === TimeSlotMode.Phone
                    ? 'bg-primary text-white'
                    : 'bg-[rgba(40,25,10,0.1)]',
                )}
              >
                <span>{time}</span>
                <span>Uhr</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>

    <div
      className={classNames(
        'my-12 flex flex-col justify-center gap-2',
        !stackButtons && 'md:flex-row md:gap-8',
      )}
    >
      <Button
        size="lg"
        variant="secondary"
        onClick={onBack}
        className={classNames('w-full', !stackButtons && 'md:w-sm')}
      >
        {backLabel}
      </Button>
      <Button
        size="lg"
        disabled={!selectedTime}
        onClick={onNext}
        className={classNames('w-full', !stackButtons && 'md:w-sm')}
      >
        {ctaLabel}
      </Button>
    </div>
  </div>
);
